import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { LoadingSpinner } from '../../components';
import { useAuth } from '../../contexts/AuthContext';
import { accountsApi } from '../../utils/accountsApi';
import { vouchersApi } from '../../utils/vouchersApi';
import './DashboardPage.css';

/* ── Stat card atom ─────────────────────────────────────────────────────── */

const StatCard = ({ icon, label, value, color, loading }) => (
  <div className={`db-stat db-stat--${color}`}>
    <div className="db-stat__icon">
      <i className={icon} aria-hidden="true" />
    </div>
    <div className="db-stat__body">
      <p className="db-stat__label">{label}</p>
      {loading ? (
        <div className="db-stat__spin">
          <LoadingSpinner size="small" variant="button" />
        </div>
      ) : (
        <p className="db-stat__value">{value}</p>
      )}
    </div>
  </div>
);

/* ── Custom tooltip for recharts ────────────────────────────────────────── */
const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="db-chart-tip">
      <span className="db-chart-tip__name">{payload[0].name}</span>
      <span className="db-chart-tip__val">{payload[0].value.toLocaleString()}</span>
    </div>
  );
};

/* ── Main page ──────────────────────────────────────────────────────────── */

const VOUCHER_COLORS = { JV: '#2563eb', RV: '#16a34a', PV: '#d97706' };
const ACCOUNT_COLORS = ['#1a4f8c', '#3498db'];

const DashboardPage = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();

  const canViewVouchers  = hasPermission('vouchers.view');
  const canCreateVoucher = hasPermission('vouchers.create');
  const canViewAccounts  = hasPermission('accounts.view');
  const canViewReports   = hasPermission('reports.view');

  const [loading, setLoading] = useState(true);
  const [accountStats,   setAccountStats]   = useState(null);
  const [voucherTotal,   setVoucherTotal]   = useState(null);
  const [draftCount,     setDraftCount]     = useState(null);
  const [postedCount,    setPostedCount]    = useState(null);
  const [recentVouchers, setRecentVouchers] = useState([]);
  const [jvCount,        setJvCount]        = useState(null);
  const [rvCount,        setRvCount]        = useState(null);
  const [pvCount,        setPvCount]        = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [accRes, allRes, draftRes, postedRes, jvRes, rvRes, pvRes] =
          await Promise.allSettled([
            canViewAccounts  ? accountsApi.stats()                                  : Promise.resolve(null),
            canViewVouchers  ? vouchersApi.list({ limit: 5 })                       : Promise.resolve(null),
            canViewVouchers  ? vouchersApi.list({ status: 'DRAFT',  limit: 1 })     : Promise.resolve(null),
            canViewVouchers  ? vouchersApi.list({ status: 'POSTED', limit: 1 })     : Promise.resolve(null),
            canViewVouchers  ? vouchersApi.list({ type: 'JV',       limit: 1 })     : Promise.resolve(null),
            canViewVouchers  ? vouchersApi.list({ type: 'RV',       limit: 1 })     : Promise.resolve(null),
            canViewVouchers  ? vouchersApi.list({ type: 'PV',       limit: 1 })     : Promise.resolve(null),
          ]);

        if (cancelled) return;

        setAccountStats(accRes.status   === 'fulfilled' ? accRes.value   : null);
        const all  = allRes.status   === 'fulfilled' ? allRes.value   : null;
        setVoucherTotal(all?.total ?? null);
        setRecentVouchers(all?.items   ?? []);
        setDraftCount( draftRes.status  === 'fulfilled' ? (draftRes.value?.total  ?? null) : null);
        setPostedCount(postedRes.status === 'fulfilled' ? (postedRes.value?.total ?? null) : null);
        setJvCount(    jvRes.status     === 'fulfilled' ? (jvRes.value?.total     ?? 0)    : 0);
        setRvCount(    rvRes.status     === 'fulfilled' ? (rvRes.value?.total     ?? 0)    : 0);
        setPvCount(    pvRes.status     === 'fulfilled' ? (pvRes.value?.total     ?? 0)    : 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [canViewVouchers, canViewAccounts]);

  const firstName  = (user?.fullName || user?.username || 'User').split(' ')[0];
  const fmtNum = (n) => (n == null ? '—' : n.toLocaleString());

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  /* ── Chart data ── */
  const voucherTypeData = [
    { name: 'Journal (JV)',  value: jvCount || 0, color: VOUCHER_COLORS.JV },
    { name: 'Receipt (RV)', value: rvCount || 0, color: VOUCHER_COLORS.RV },
    { name: 'Payment (PV)', value: pvCount || 0, color: VOUCHER_COLORS.PV },
  ].filter(d => d.value > 0);

  const accountTypeData = accountStats
    ? [
        { name: 'Posting Accounts', value: accountStats.postingAccounts || 0, color: ACCOUNT_COLORS[0] },
        { name: 'Header Accounts',  value: accountStats.headerAccounts  || 0, color: ACCOUNT_COLORS[1] },
      ].filter(d => d.value > 0)
    : [];

  const showCharts = !loading && (
    (canViewVouchers && voucherTypeData.length > 0) ||
    (canViewAccounts && accountTypeData.length > 0)
  );

  return (
    <div className="db-page">

      {/* ── Welcome banner ─────────────────────────────────────────────── */}
      <div className="db-welcome">
        <div className="db-welcome__text">
          <h2 className="db-welcome__title">Welcome back, {firstName}!</h2>
          <p className="db-welcome__date">{today}</p>
        </div>
        {canCreateVoucher && (
          <button className="db-welcome__cta" onClick={() => navigate('/vouchers/new')}>
            <i className="fas fa-plus" aria-hidden="true"></i>
            New Voucher
          </button>
        )}
      </div>

      {/* ── KPI stats ──────────────────────────────────────────────────── */}
      {(canViewVouchers || canViewAccounts) && (
        <div className="db-stats">
          {canViewVouchers && (
            <>
              <StatCard icon="fas fa-file-invoice-dollar" label="Total Vouchers"  value={fmtNum(voucherTotal)} color="blue"   loading={loading} />
              <StatCard icon="fas fa-check-circle"        label="Posted"          value={fmtNum(postedCount)}  color="green"  loading={loading} />
              <StatCard icon="fas fa-clock"               label="Draft"           value={fmtNum(draftCount)}   color="amber"  loading={loading} />
            </>
          )}
          {canViewAccounts && (
            <StatCard icon="fas fa-book" label="Chart of Accounts" value={fmtNum(accountStats?.totalAccounts)} color="indigo" loading={loading} />
          )}
        </div>
      )}

      {/* ── Body grid ──────────────────────────────────────────────────── */}
      <div className="db-grid">

        {/* Quick actions */}
        <div className="db-card">
          <h3 className="db-card__title">
            <i className="fas fa-bolt" aria-hidden="true"></i>
            Quick Actions
          </h3>
          <div className="db-qa-list">
            {canCreateVoucher && (
              <Link to="/vouchers/new" className="db-qa-item">
                <i className="fas fa-plus-circle" aria-hidden="true"></i>
                <span>New Voucher</span>
              </Link>
            )}
            {canViewVouchers && (
              <Link to="/vouchers" className="db-qa-item">
                <i className="fas fa-list-alt" aria-hidden="true"></i>
                <span>All Vouchers</span>
              </Link>
            )}
            {canViewReports && (
              <Link to="/reports/trial-balance" className="db-qa-item">
                <i className="fas fa-balance-scale" aria-hidden="true"></i>
                <span>Trial Balance</span>
              </Link>
            )}
            {canViewReports && (
              <Link to="/reports/pnl" className="db-qa-item">
                <i className="fas fa-chart-line" aria-hidden="true"></i>
                <span>Profit &amp; Loss</span>
              </Link>
            )}
            {canViewReports && (
              <Link to="/reports/balance-sheet" className="db-qa-item">
                <i className="fas fa-columns" aria-hidden="true"></i>
                <span>Balance Sheet</span>
              </Link>
            )}
            {canViewAccounts && (
              <Link to="/chart-of-accounts" className="db-qa-item">
                <i className="fas fa-book" aria-hidden="true"></i>
                <span>Chart of Accounts</span>
              </Link>
            )}
          </div>
        </div>

        {/* Recent vouchers */}
        {canViewVouchers ? (
          <div className="db-card db-card--wide">
            <div className="db-card__header">
              <h3 className="db-card__title">
                <i className="fas fa-history" aria-hidden="true"></i>
                Recent Vouchers
              </h3>
              <Link to="/vouchers" className="db-card__link">View all →</Link>
            </div>

            {loading ? (
              <LoadingSpinner size="small" message="Loading vouchers…" />
            ) : recentVouchers.length === 0 ? (
              <p className="db-empty">No vouchers recorded yet.</p>
            ) : (
              <div className="db-vlist">
                {recentVouchers.map((v) => (
                  <Link to={`/vouchers/${v.id}`} key={v.id} className="db-vrow">
                    <span className={`db-vtype db-vtype--${(v.type || '').toLowerCase()}`}>
                      {v.type || '—'}
                    </span>
                    <span className="db-vnum">{v.voucherNumber || v.reference || '—'}</span>
                    <span className="db-vdesc">{v.description || '—'}</span>
                    <span className="db-vdate">
                      {v.date
                        ? new Date(v.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                        : '—'}
                    </span>
                    <span className={`db-vstatus db-vstatus--${(v.status || '').toLowerCase()}`}>
                      {v.status || '—'}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="db-card db-card--wide db-card--muted">
            <div className="db-no-access">
              <i className="fas fa-lock" aria-hidden="true"></i>
              <p>You don't have access to voucher data.</p>
              <p>Contact your administrator to request access.</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Charts row ─────────────────────────────────────────────────── */}
      {showCharts && (
        <div className="db-charts">

          {canViewVouchers && voucherTypeData.length > 0 && (
            <div className="db-card db-chart-card">
              <h3 className="db-card__title">
                <i className="fas fa-chart-pie" aria-hidden="true"></i>
                Voucher Distribution
              </h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={voucherTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={64}
                    outerRadius={96}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {voucherTypeData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend
                    iconType="circle"
                    iconSize={10}
                    formatter={(v) => <span style={{ fontSize: '0.8rem', color: '#475063' }}>{v}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="db-chart-legend">
                {voucherTypeData.map(d => (
                  <div key={d.name} className="db-chart-legend__item">
                    <span className="db-chart-legend__dot" style={{ background: d.color }} />
                    <span className="db-chart-legend__label">{d.name}</span>
                    <span className="db-chart-legend__val">{d.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {canViewAccounts && accountTypeData.length > 0 && (
            <div className="db-card db-chart-card">
              <h3 className="db-card__title">
                <i className="fas fa-book" aria-hidden="true"></i>
                Account Structure
              </h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={accountTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={64}
                    outerRadius={96}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {accountTypeData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend
                    iconType="circle"
                    iconSize={10}
                    formatter={(v) => <span style={{ fontSize: '0.8rem', color: '#475063' }}>{v}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="db-chart-legend">
                {accountTypeData.map(d => (
                  <div key={d.name} className="db-chart-legend__item">
                    <span className="db-chart-legend__dot" style={{ background: d.color }} />
                    <span className="db-chart-legend__label">{d.name}</span>
                    <span className="db-chart-legend__val">{d.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}

    </div>
  );
};

export default DashboardPage;
